import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import './PartnersStrip.css';

// Shown when the agencies table has fewer than 8 real entries
const EXAMPLE_PARTNERS = [
  { id: 'ex-1', name: 'Pafilia Property Developers', cities_served: ['Paphos'],    logo_url: null },
  { id: 'ex-2', name: 'Leptos Estates',              cities_served: ['Limassol'],  logo_url: null },
  { id: 'ex-3', name: 'Cybarco',                     cities_served: ['Nicosia'],   logo_url: null },
  { id: 'ex-4', name: 'Imperio Properties',          cities_served: ['Limassol'],  logo_url: null },
  { id: 'ex-5', name: 'Korantina Homes',             cities_served: ['Paphos'],    logo_url: null },
  { id: 'ex-6', name: 'Quality Group',               cities_served: ['Larnaca'],   logo_url: null },
  { id: 'ex-7', name: 'Giovani Group',               cities_served: ['Limassol'],  logo_url: null },
  { id: 'ex-8', name: 'Atlas Homes',                 cities_served: ['Famagusta'], logo_url: null },
];

function PartnerCard({ partner, displayName }) {
  const [imgFailed, setImgFailed] = useState(false);
  const rawImage = partner.logo_url;
  const image = rawImage
    ? (rawImage.includes('supabase') ? `${rawImage}?v=2` : rawImage)
    : null;
  const showImg = !!image && !imgFailed;
  const isExample = String(partner.id).startsWith('ex-');
  const label = displayName || partner.name;

  return (
    <Link
      to={isExample ? '/partners' : `/listings?company=${encodeURIComponent(partner.name)}`}
      className="partners__card"
    >
      <div className="partners__cardBanner">
        {showImg ? (
          <img
            src={image}
            alt={label}
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="partners__cardBannerPlaceholder">
            <span className="partners__cardInitial">
              {label?.charAt(0) || '?'}
            </span>
          </div>
        )}
      </div>
      <div className="partners__cardCaption">
        <h3 className="partners__cardTitle">{label}</h3>
        {partner.cities_served?.length > 0 && (
          <span className="partners__cardLocation">{partner.cities_served[0]}</span>
        )}
      </div>
    </Link>
  );
}

function ensureScrollable(row) {
  if (row.length === 0) return row;
  const repeats = Math.ceil(6 / row.length);
  return repeats > 1 ? Array(repeats).fill(row).flat() : row;
}

export default function PartnersStrip() {
  const { t } = useLanguage();
  const [dbPartners, setDbPartners] = useState([]);

  useEffect(() => {
    supabase
      .from('agencies')
      .select('id, name, slug, logo_url, cities_served')
      .order('created_at', { ascending: true })
      .limit(20)
      .then(({ data }) => {
        setDbPartners(
          (data || []).map(a => ({
            id:            a.id,
            name:          a.name,
            slug:          a.slug || a.id,
            logo_url:      a.logo_url,
            cities_served: a.cities_served || [],
          }))
        );
      });
  }, []);

  const partners = useMemo(() => {
    // Fill with examples until we have 8 real entries (excluding any already in DB)
    const fillers = EXAMPLE_PARTNERS.filter(
      ex => !dbPartners.some(r => r.name === ex.name)
    ).slice(0, Math.max(0, 8 - dbPartners.length));
    return [...dbPartners, ...fillers].sort((a, b) => {
      if (a.name === 'ARISTO DEVELOPERS') return -1;
      if (b.name === 'ARISTO DEVELOPERS') return 1;
      return 0;
    });
  }, [dbPartners]);

  const scrollableRow1 = ensureScrollable(partners);
  const rowLen = scrollableRow1.length;

  return (
    <div className="partners">
      <div className="partners__header">
        <div className="partners__eyebrow">
          <span className="partners__eyebrowLine" />
          <span className="partners__eyebrowText">{t('partners.eyebrow')}</span>
          <span className="partners__eyebrowLine" />
        </div>
        <h2 className="partners__title">{t('partners.title')}</h2>
        <p className="partners__subtitle">
          {t('partners.subtitle')}{' '}
          <Link to="/partners" className="partners__subtitleLink">{t('partners.discoverAll')}</Link>
        </p>
      </div>

      <div className="partners__marquee">
        <div className="partners__row partners__row--leftward">
          <div className="partners__track">
            {[...scrollableRow1, ...scrollableRow1].map((p, i) => (
              <PartnerCard
                key={`r1-${p.id}-${i}`}
                partner={p}
                displayName={`Partner ${(i % rowLen) + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
